import { useState, useEffect } from 'react'
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  Chip,
  Alert,
  Typography,
  LinearProgress,
} from '@mui/material'
import { Add, Edit, Delete, Warning, Visibility, AttachFile } from '@mui/icons-material'
import { PageHeader, Button, DataTable, StatusChip, Card, Modal } from '@components/common'
import { toast } from 'react-toastify'
import api from '@services/api'

const CONTRACT_TYPES = [
  { value: 'warranty', label: 'Warranty' },
  { value: 'maintenance', label: 'Maintenance Contract' },
  { value: 'calibration', label: 'Calibration Contract' },
  { value: 'full_service', label: 'Full Service Contract' },
]

const ContractsList = () => {
  const [contracts, setContracts] = useState([])
  const [assets, setAssets] = useState([])
  const [vendors, setVendors] = useState([])
  const [loading, setLoading] = useState(true)
  const [formOpen, setFormOpen] = useState(false)
  const [detailOpen, setDetailOpen] = useState(false)
  const [selectedContract, setSelectedContract] = useState(null)
  const [expiringSoon, setExpiringSoon] = useState([])
  const [saving, setSaving] = useState(false)
  const [formData, setFormData] = useState({
    asset: '',
    vendor: '',
    contract_number: '',
    contract_type: 'maintenance',
    start_date: '',
    end_date: '',
    cost: '',
    terms: '',
    is_active: true,
  })

  const fetchData = async () => {
    try {
      setLoading(true)
      const [contractsRes, assetsRes, vendorsRes] = await Promise.all([
        api.get('/assets/contracts/'),
        api.get('/assets/assets/'),
        api.get('/vendors/'),
      ])
      const contractsData = contractsRes.data.results || contractsRes.data
      setContracts(contractsData)
      setAssets(assetsRes.data.results || assetsRes.data)
      setVendors(vendorsRes.data.results || vendorsRes.data)

      // Calculate expiring soon (within 30 days)
      const today = new Date()
      const thirtyDays = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000)
      const expiring = contractsData.filter((c) => {
        if (!c.end_date || !c.is_active) return false
        const endDate = new Date(c.end_date)
        return endDate <= thirtyDays && endDate >= today
      })
      setExpiringSoon(expiring)
    } catch (error) {
      console.error('Error fetching data:', error)
      toast.error('Failed to load contracts data')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const handleAdd = () => {
    setSelectedContract(null)
    const today = new Date().toISOString().split('T')[0]
    const nextYear = new Date()
    nextYear.setFullYear(nextYear.getFullYear() + 1)
    setFormData({
      asset: '',
      vendor: '',
      contract_number: '',
      contract_type: 'maintenance',
      start_date: today,
      end_date: nextYear.toISOString().split('T')[0],
      cost: '',
      terms: '',
      is_active: true,
    })
    setFormOpen(true)
  }

  const handleEdit = (contract) => {
    setSelectedContract(contract)
    setFormData({
      asset: contract.asset || '',
      vendor: contract.vendor || '',
      contract_number: contract.contract_number || '',
      contract_type: contract.contract_type || 'maintenance',
      start_date: contract.start_date || '',
      end_date: contract.end_date || '',
      cost: contract.cost || '',
      terms: contract.terms || '',
      is_active: contract.is_active !== false,
    })
    setFormOpen(true)
  }

  const handleView = (contract) => {
    setSelectedContract(contract)
    setDetailOpen(true)
  }

  const handleSave = async () => {
    if (!formData.asset || !formData.contract_number || !formData.start_date || !formData.end_date) {
      toast.error('Please fill in required fields')
      return
    }

    setSaving(true)
    try {
      const payload = {
        ...formData,
        vendor: formData.vendor || null,
        cost: formData.cost || null,
      }
      if (selectedContract) {
        await api.patch(`/assets/contracts/${selectedContract.id}/`, payload)
        toast.success('Contract updated')
      } else {
        await api.post('/assets/contracts/', payload)
        toast.success('Contract created')
      }
      setFormOpen(false)
      fetchData()
    } catch (error) {
      console.error('Error saving contract:', error)
      toast.error(error.response?.data?.error || 'Failed to save contract')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (contract) => {
    if (!window.confirm(`Delete contract ${contract.contract_number}?`)) return
    try {
      await api.delete(`/assets/contracts/${contract.id}/`)
      toast.success('Contract deleted')
      fetchData()
    } catch (error) {
      console.error('Error deleting contract:', error)
      toast.error('Failed to delete contract')
    }
  }

  const getContractStatus = (contract) => {
    if (!contract.is_active) return { label: 'Inactive', color: 'default' }
    if (!contract.end_date) return { label: 'Active', color: 'success' }

    const today = new Date()
    const endDate = new Date(contract.end_date)
    const daysUntil = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24))

    if (daysUntil < 0) return { label: 'Expired', color: 'error' }
    if (daysUntil <= 30) return { label: `${daysUntil}d left`, color: 'warning' }
    return { label: 'Active', color: 'success' }
  }

  const columns = [
    { field: 'contract_number', headerName: 'Contract #', sortable: true },
    {
      field: 'contract_type',
      headerName: 'Type',
      renderCell: (v) => (
        <Chip label={CONTRACT_TYPES.find((t) => t.value === v)?.label || v} size="small" />
      ),
    },
    {
      field: 'asset',
      headerName: 'Asset',
      renderCell: (_, row) => row.asset_name || '-',
    },
    {
      field: 'vendor',
      headerName: 'Vendor',
      renderCell: (_, row) => row.vendor_name || '-',
    },
    {
      field: 'start_date',
      headerName: 'Start',
      renderCell: (v) => v ? new Date(v).toLocaleDateString() : '-',
      sortable: true,
    },
    {
      field: 'end_date',
      headerName: 'End',
      renderCell: (v) => v ? new Date(v).toLocaleDateString() : '-',
      sortable: true,
    },
    {
      field: 'cost',
      headerName: 'Cost',
      renderCell: (v) => v ? `UGX ${Number(v).toLocaleString()}` : '-',
    },
    {
      field: 'status',
      headerName: 'Status',
      renderCell: (_, row) => {
        const status = getContractStatus(row)
        return <Chip label={status.label} size="small" color={status.color} />
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 120,
      renderCell: (_, row) => (
        <Box display="flex" gap={0.5}>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleView(row) }}>
            <Visibility fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEdit(row) }}>
            <Edit fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDelete(row) }}>
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ]

  // Summary stats
  const activeContracts = contracts.filter((c) => c.is_active).length
  const expiringContracts = expiringSoon.length
  const totalValue = contracts.reduce((sum, c) => sum + (parseFloat(c.cost) || 0), 0)

  return (
    <Box>
      <PageHeader
        title="Service Contracts"
        subtitle="Manage asset service contracts and warranties"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Vendors', path: '/vendors' },
          { label: 'Contracts' },
        ]}
        actions={
          <Button startIcon={<Add />} onClick={handleAdd}>
            Add Contract
          </Button>
        }
      />

      {/* Summary Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={4}>
          <Card>
            <Box textAlign="center">
              <Typography variant="h4" fontWeight={700} color="primary">
                {activeContracts}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active Contracts
              </Typography>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <Box textAlign="center">
              <Typography variant="h4" fontWeight={700} color="warning.main">
                {expiringContracts}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Expiring Soon
              </Typography>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <Box textAlign="center">
              <Typography variant="h4" fontWeight={700} color="success.main">
                UGX {totalValue.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Contract Value
              </Typography>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {expiringSoon.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }} icon={<Warning />}>
          {expiringSoon.length} contract(s) expiring within the next 30 days
        </Alert>
      )}

      <Card>
        <DataTable
          columns={columns}
          data={contracts}
          loading={loading}
          searchPlaceholder="Search contracts..."
          emptyMessage="No contracts found"
        />
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={selectedContract ? 'Edit Contract' : 'Add Contract'}
        maxWidth="sm"
        actions={
          <>
            <Button variant="outlined" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} loading={saving}>
              {selectedContract ? 'Update' : 'Create'}
            </Button>
          </>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth required>
              <InputLabel>Asset</InputLabel>
              <Select
                value={formData.asset}
                onChange={(e) => setFormData({ ...formData, asset: e.target.value })}
                label="Asset"
              >
                {assets.map((a) => (
                  <MenuItem key={a.id} value={a.id}>{a.name} ({a.asset_tag})</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Contract Number"
              value={formData.contract_number}
              onChange={(e) => setFormData({ ...formData, contract_number: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>Contract Type</InputLabel>
              <Select
                value={formData.contract_type}
                onChange={(e) => setFormData({ ...formData, contract_type: e.target.value })}
                label="Contract Type"
              >
                {CONTRACT_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Vendor</InputLabel>
              <Select
                value={formData.vendor}
                onChange={(e) => setFormData({ ...formData, vendor: e.target.value })}
                label="Vendor"
              >
                <MenuItem value="">None</MenuItem>
                {vendors.filter((v) => v.is_active).map((v) => (
                  <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Start Date"
              type="date"
              value={formData.start_date}
              onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="End Date"
              type="date"
              value={formData.end_date}
              onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Contract Value (UGX)"
              type="number"
              value={formData.cost}
              onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Terms & Conditions"
              value={formData.terms}
              onChange={(e) => setFormData({ ...formData, terms: e.target.value })}
              multiline
              rows={3}
            />
          </Grid>
        </Grid>
      </Modal>

      {/* Detail Modal */}
      <Modal
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        title="Contract Details"
        maxWidth="sm"
        actions={
          <Button variant="outlined" onClick={() => setDetailOpen(false)}>Close</Button>
        }
      >
        {selectedContract && (
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">Contract Number</Box>
                <Box fontWeight={600}>{selectedContract.contract_number}</Box>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">Type</Box>
                <Chip
                  label={CONTRACT_TYPES.find((t) => t.value === selectedContract.contract_type)?.label}
                  size="small"
                  sx={{ mt: 0.5 }}
                />
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">Asset</Box>
                <Box fontWeight={600}>{selectedContract.asset_name || '-'}</Box>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">Vendor</Box>
                <Box fontWeight={600}>{selectedContract.vendor_name || '-'}</Box>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">Start Date</Box>
                <Box fontWeight={600}>
                  {selectedContract.start_date
                    ? new Date(selectedContract.start_date).toLocaleDateString()
                    : '-'}
                </Box>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">End Date</Box>
                <Box fontWeight={600}>
                  {selectedContract.end_date
                    ? new Date(selectedContract.end_date).toLocaleDateString()
                    : '-'}
                </Box>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">Value</Box>
                <Box fontWeight={600}>
                  {selectedContract.cost ? `UGX ${Number(selectedContract.cost).toLocaleString()}` : '-'}
                </Box>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">Status</Box>
                <Chip
                  label={getContractStatus(selectedContract).label}
                  size="small"
                  color={getContractStatus(selectedContract).color}
                  sx={{ mt: 0.5 }}
                />
              </Box>
            </Grid>
            {selectedContract.terms && (
              <Grid item xs={12}>
                <Box>
                  <Box color="text.secondary" fontSize="0.75rem">Terms & Conditions</Box>
                  <Box mt={0.5}>{selectedContract.terms}</Box>
                </Box>
              </Grid>
            )}
          </Grid>
        )}
      </Modal>
    </Box>
  )
}

export default ContractsList
